import { registerPlugin } from '@capacitor/core';
import { defineNuxtPlugin } from '#imports';

// Type definitions
export interface MicrophonePermissionStatus {
  granted: boolean;
  available: boolean;
}

export interface MicrophoneDeviceInfo {
  id: number;
  type: string;
  name: string;
}

export interface MicrophoneInfo {
  micMuted: boolean;
  audioMode: string;
  speakerphoneOn: boolean;
  bluetoothScoOn: boolean;
  devices?: MicrophoneDeviceInfo[];
}

export interface AudioModeSetupResult {
  success: boolean;
}

export interface MicrophonePermissionPlugin {
  checkPermission(): Promise<MicrophonePermissionStatus>;
  requestPermission(): Promise<MicrophonePermissionStatus>;
  openSettings(): Promise<void>;
  getMicrophoneInfo(): Promise<MicrophoneInfo>;
  setupAudioMode(): Promise<AudioModeSetupResult>;
}

// Web implementation for the plugin
class MicrophonePermissionWeb {
  async checkPermission(): Promise<MicrophonePermissionStatus> {
    let granted = false;
    let available = false;
    
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
            granted = permissionStatus.state === 'granted';
          } catch (err) {
            console.warn('Permissions API not fully supported, falling back to getUserMedia test');
          }
        }
        
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        granted = true;
        available = true;
        stream.getTracks().forEach(track => track.stop());
      } catch (err: any) {
        if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
          granted = false;
          available = false;
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          granted = true;
          available = false;
        }
      }
    }
    
    return { granted, available };
  }
  
  async requestPermission(): Promise<MicrophonePermissionStatus> {
    return this.checkPermission(); // In web, this is the same as checking
  }
  
  async openSettings(): Promise<void> {
    console.warn('openSettings is not available in web context');
  }
  
  async getMicrophoneInfo(): Promise<MicrophoneInfo> {
    let devices: MicrophoneDeviceInfo[] = [];
    
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      try {
        const deviceList = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = deviceList.filter(device => device.kind === 'audioinput');
        
        devices = audioDevices.map((device, index) => ({
          id: index,
          type: 'WEB_AUDIOINPUT',
          name: device.label || `Microphone ${index + 1}`
        }));
      } catch (err) {
        console.warn('Failed to enumerate audio devices:', err);
      }
    }
    
    return {
      micMuted: false,
      audioMode: 'WEB',
      speakerphoneOn: true,
      bluetoothScoOn: false,
      devices
    };
  }
  
  async setupAudioMode(): Promise<AudioModeSetupResult> {
    return { success: true };
  }
}

// Register plugin with Capacitor
const MicrophonePermission = registerPlugin<MicrophonePermissionPlugin>('MicrophonePermission', {
  web: () => Promise.resolve(new MicrophonePermissionWeb()),
});

// Service class for managing permissions
export class MicrophonePermissionService {
  private static instance: MicrophonePermissionService;
  private permissionStatus: MicrophonePermissionStatus | null = null;
  private permissionCheckPromise: Promise<MicrophonePermissionStatus> | null = null;
  
  private constructor() {
    // Private constructor to enforce singleton pattern
  }
  
  public static getInstance(): MicrophonePermissionService {
    if (!MicrophonePermissionService.instance) {
      MicrophonePermissionService.instance = new MicrophonePermissionService();
    }
    return MicrophonePermissionService.instance;
  }
  
  public async checkPermission(force: boolean = false): Promise<MicrophonePermissionStatus> {
    if (this.permissionStatus && !force) {
      return this.permissionStatus;
    }
    
    if (this.permissionCheckPromise && !force) {
      return this.permissionCheckPromise;
    }
    
    this.permissionCheckPromise = MicrophonePermission.checkPermission();
    
    try {
      this.permissionStatus = await this.permissionCheckPromise;
      return this.permissionStatus;
    } finally {
      this.permissionCheckPromise = null;
    }
  }
  
  public async requestPermission(): Promise<MicrophonePermissionStatus> {
    this.permissionStatus = null;
    this.permissionCheckPromise = null;
    
    const result = await MicrophonePermission.requestPermission();
    this.permissionStatus = result;
    
    return result;
  }
  
  public async isMicrophoneReady(): Promise<boolean> {
    const status = await this.checkPermission();
    return status.granted && status.available;
  }
  
  public async ensureMicrophoneReady(): Promise<boolean> {
    const status = await this.checkPermission();
    
    if (status.granted && status.available) {
      return true;
    }
    
    if (!status.granted) {
      const requestResult = await this.requestPermission();
      
      if (!requestResult.granted) {
        return false;
      }
      
      if (!requestResult.available) {
        await this.setupAudioMode();
        
        const finalCheck = await this.checkPermission(true);
        return finalCheck.granted && finalCheck.available;
      }
      
      return true;
    }
    
    if (status.granted && !status.available) {
      await this.setupAudioMode();
      const recheck = await this.checkPermission(true);
      return recheck.granted && recheck.available;
    }
    
    return false;
  }
  
  public async getMicrophoneInfo(): Promise<MicrophoneInfo> {
    return MicrophonePermission.getMicrophoneInfo();
  }
  
  public async setupAudioMode(): Promise<boolean> {
    const result = await MicrophonePermission.setupAudioMode();
    return result.success;
  }
  
  public async openSettings(): Promise<void> {
    return MicrophonePermission.openSettings();
  }
  
  public clearCache(): void {
    this.permissionStatus = null;
    this.permissionCheckPromise = null;
  }
}

// Export plugin and service
export { MicrophonePermission };

// Define Nuxt plugin
export default defineNuxtPlugin(() => {
  return {
    provide: {
      microphonePermission: MicrophonePermissionService.getInstance()
    }
  };
}); 